/* eslint-disable no-unused-vars */
import { http } from '@/plugins/http'
import { v1 as uuid } from 'uuid'
import b64toBlob from 'b64-to-blob'
import { checkStatus, getHeaders, parseJSON, buildUrlWithQuery, buildUrl } from '@/utils/api'
import { normalize, schema } from 'normalizr'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  RECEIVE_TRANSACTIONS,
  RECEIVING_TRANSACTIONS,
  RECEIVE_REPORT,
  RECEIVING_REPORT,
  RECEIVING_REPORTS,
  RECEIVE_REPORT_METADATA,
  RECEIVE_REPORT_FAILURE,
  RECEIVE_REPORTS_FAILURE,
  SET_REPORT_VIEW_STATUS,
  UPDATE_TRANSACTION_SELECTION,
  UPDATE_FILTERED_TRANSACTION_SELECTION,
  UPDATE_TRANSACTIONS_SHOWN,
  UPDATE_CURRENT_REPORT_ID,
  CLEAR_TRANSACTIONS,
  CACHING_PARAMS,
  CACHE_PARAM_FAILURE,
  RECEIVE_CACHED_PARAMS,
  RECEIVING_CACHED_PARAMS,
  RECEIVE_CACHED_PARAMS_FAILURE,
  RENDERING_REPORT,
  RESET_REPORT_STATE,
  SET_POST_BTN_VISIBILITY,
  CLEAR_STATE
} from '../mutationTypes'

const reportSchema = new schema.Entity('reports')
const reportListSchema = new schema.Array(reportSchema)
const cachedParamSchema = new schema.Entity('cachedParams')

const state = {
  data: {},
  report: {},
  receivingReport: null,
  receivingReports: false,
  renderingReport: false,
  receiveReportFailure: null,
  receiveReportsFailure: null,
  reportViewStatus: {},
  cachedParams: {},
  cachingParams: { status: false, type: '', reportId: '' },
  cacheParamFailure: false,
  receivingCachedParams: false,
  receiveCachedParamsFailure: false,
  triggerReportAction: false,
  transactions: [],
  receivingTransactions: false,
  transactionSelection: [],
  filteredTransactionSelection: [],
  transactionsShown: [],
  currentReportId: null,
  postBtnVisible: true,
  reportPdfUrl: null
}

const actions = {
  /**
   * Gets the first page of APs and JEs, as well as additional pages if necessary
   * @param store
   * @param payload
   * @return {Promise.<TResult>}
   */
  async fetchTransactionsWithQuery (store, payload) {
    try {
      let promises = [
        store.dispatch('fetchJesWithQuery', { status__in: ['OPEN', 'AUDITED'], ...payload })
          .then(r => {
            this.commit(RECEIVE_TRANSACTIONS, { type: 'jes', data: r.entities.jes })
            return r
          })
          .catch(e => console.log(e)),
        store.dispatch('fetchApsWithQuery', { status__in: ['OPEN', 'AUDITED'], ...payload })
          .then(r => {
            this.commit(RECEIVE_TRANSACTIONS, { type: 'aps', data: r.entities.aps })
            return r
          })
          .catch(e => console.log(e))
      ]
      this.commit(RECEIVING_TRANSACTIONS, true)
      const resp = await Promise.all(promises)
      let requests = []   // Get additional pages, if applicable
      if (resp && resp.length) {
        const getRequestsNeeded = (count, firstPageLength) => Math.ceil(count / firstPageLength) - 1
        const loadRequests = (needed, fetchAction, type) => {
          for (let i = 2; i <= needed + 1; i++) {
            requests.push(
              store.dispatch(fetchAction, { page: i, status__in: ['OPEN', 'AUDITED'], ...payload })
                .then(r => {
                  this.commit(RECEIVE_TRANSACTIONS, { type: type, data: r.entities[type] })
                  return r
                })
                .catch(e => console.log(e))
            )
          }
        }
        const { getJes, getAps, getJeMetadata, getApMetadata } = store.getters
        if (getJeMetadata.count && getJeMetadata.next) {
          const jeRequestsNeeded = getRequestsNeeded(getJeMetadata.count, Object.keys(getJes).length)
          if (jeRequestsNeeded) {
            loadRequests(jeRequestsNeeded, 'fetchJesWithQuery', 'jes')
          }
        }
        if (getApMetadata.count && getApMetadata.next) {
          const apRequestsNeeded = getRequestsNeeded(getApMetadata.count, Object.keys(getAps).length)
          if (apRequestsNeeded) {
            loadRequests(apRequestsNeeded, 'fetchApsWithQuery', 'aps')
          }
        }
      }
      const pageResps = await Promise.all(requests)
      this.commit(RECEIVING_TRANSACTIONS, false)
      return [...resp, ...pageResps]
    } catch (e) {
      console.log(e)
      this.commit(RECEIVING_TRANSACTIONS, false)
      return e
    }
  },
  /**
   * Fetches dependencies for selected transactions
   * @param store
   */
  fetchTransactionDependencies (store) {
    const postable = store.getters.getPostableTransactions
    const postableAps = postable.filter(v => v.transactionType === 'Accounts Payable')
    const postableJes = postable.filter(v => v.transactionType === 'Journal Entry')
    const entities = { aps: postableAps, jes: postableJes }
    store.dispatch('findDependencies', entities)
      .then(promises => Promise.all(promises))
      .catch(e => console.log(e))
  },
  /**
   * Caches report params
   * @param store
   * @param payload
   * @return {Promise.<T>|Promise|*}
   */
  cacheParams (store, payload) {
    const reportId = uuid()
    const { data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return http.post(buildUrlWithQuery('reports/params', null, instanceUrl), { data }, { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (!r) return this.commit(CACHE_PARAM_FAILURE, 'Error caching params')
        r['reportId'] = reportId
        const { entities, result } = normalize(r, cachedParamSchema)
        if (!result || (Array.isArray(result) && !result.length)) {
          return this.commit(CACHE_PARAM_FAILURE, 'Error normalizing response')
        }
        this.commit(RECEIVE_CACHED_PARAMS, entities.cachedParams)
        this.commit(UPDATE_CURRENT_REPORT_ID, reportId)
        return r
      })
      .catch(e => {
        return this.commit(CACHE_PARAM_FAILURE, handleXHRError(e))
      })
  },
  /**
   * Retrieves cached params
   * @param store
   * @param payload
   * @return {Promise.<T>|Promise|*}
   */
  fetchCachedParams (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_CACHED_PARAMS, true)
    return http.get(buildUrlWithQuery(`reports/params/${payload}`, {}, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_CACHED_PARAMS, false)
        const { entities, result } = normalize(r, cachedParamSchema)
        if (!result) return this.commit(RECEIVE_CACHED_PARAMS_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_CACHED_PARAMS, entities.cachedParams)
        return entities
      })
      .catch(e => {
        this.commit(RECEIVING_CACHED_PARAMS, false)
        this.commit(RECEIVE_CACHED_PARAMS_FAILURE, handleXHRError(e))
      })
  },
  /**
   * Spawn the report build process once params have been cached
   * @param store
   * @param payload
   * @return {Promise.<T>|Promise|*}
   */
  startRenderingReport (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    let { query, type, reportId } = payload
    this.commit(RECEIVING_REPORT, { status: true, reportId })
    return http.get(buildUrlWithQuery(`reports/${type}`, query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (!r) return this.commit(RECEIVE_REPORT_FAILURE, 'Error building cached param report')
        r['reportId'] = reportId
        if (r.hasOwnProperty('is_async') && r.is_async && r.hasOwnProperty('param')) { // if it's async, start polling
          this.commit(RENDERING_REPORT, { renderId: r.param.renders[0].id, type, query, reportId })
        } else {
          this.commit(RECEIVING_REPORT, { status: false, reportId })
          this.commit(RECEIVE_REPORT, {
            type,
            reportId,
            data: r.data,
            transactions: {},
            originalParams: r.hasOwnProperty('param') && r.param.data ? r.param.data : {}
          })
        }
        return r
      })
      .catch(e => {
        console.log(e)
        this.commit(RECEIVING_REPORT, { status: false, reportId })
        this.commit(RECEIVE_REPORT_FAILURE, { err: e.code, msg: e.statusText, reportId })
      })
  },
  /**
   * Get the rendered report (reports/renders/<id>)
   * @param store
   * @return {Promise.<T>|Promise|*}
   */
  fetchRenderedReport (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const { renderingReport } = store.state
    const { renderId, type, query, reportId } = renderingReport
    const headers = getHeaders(this.getters.getToken)
    const url = buildUrlWithQuery(`reports/renders/${renderId}`, {}, instanceUrl)
    return http.get(url, { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (!r) return this.commit(RECEIVE_REPORT_FAILURE, 'Error fetching rendered report')
        if (r.status === 'DONE') {
          this.commit(RENDERING_REPORT, null)
          r.renderId = r.id
          r.id = reportId
          let c = store.getters.getCachedParamsById(query.cached_parameters)
          http.get(`${url}/body`, { headers })
            .then(checkStatus)
            .then(parseJSON)
            .then(r => {
              if (r.hasOwnProperty('body') && r.body) {
                this.commit(RECEIVING_REPORT, { status: false, reportId: store.state.currentReportId })
                this.commit(RECEIVE_REPORT, {
                  type,
                  reportId,
                  data: r.body,
                  transactions: {},
                  originalParams: c ? c.data : {}
                })
              } else {
                this.commit(RECEIVE_REPORT_FAILURE, 'Error fetching report PDF body')
              }
            })
        } else if (r.status === 'ERROR') {
          return Promise.reject('Error fetching rendered report')
        }
        return r
      })
      .catch(e => {
        console.log(e)
        this.commit(RECEIVING_REPORT, { status: false, reportId: store.state.currentReportId })
        this.commit(RECEIVE_REPORT_FAILURE, handleXHRError(e))
        this.commit(RENDERING_REPORT, null)
      })
  },
  clearTransactionState (store) {
    const promises = [
      store.dispatch('clearApState'),
      store.dispatch('resetApMeta'),
      store.dispatch('clearJeState'),
      store.dispatch('resetJeMeta')
    ]
    Promise.all(promises)
      .then(() => this.commit(CLEAR_TRANSACTIONS))
  },
  setReportViewStatus (store, payload) {
    this.commit(SET_REPORT_VIEW_STATUS, payload)
  },
  updateTransactionSelection (store, payload) {
    this.commit(UPDATE_TRANSACTION_SELECTION, payload)
  },
  updateFilteredTransactionSelection (store, payload) {
    this.commit(UPDATE_FILTERED_TRANSACTION_SELECTION, payload)
  },
  updateTransactionsShown (store, payload) {
    this.commit(UPDATE_TRANSACTIONS_SHOWN, payload)
  },
  updateCurrentReportId (store, payload) {
    this.commit(UPDATE_CURRENT_REPORT_ID, payload)
  },
  resetReportRenderStatus (store) {
    this.commit(RENDERING_REPORT, null)
  },
  resetReportState (store) {
    this.commit(RESET_REPORT_STATE)
  },
  setPostBtnVisible (store, payload) {
    this.commit(SET_POST_BTN_VISIBILITY, payload)
  },
  createPayment (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return http.post(buildUrl(`create-payments`, null, instanceUrl), payload, { headers })
      .then(r => {
        if (!r || (r && r.hasOwnProperty('ok') && !r.ok)) {
          return Promise.reject(`Unable to save export id`)
        }
        return r.body
      })
      .catch(e => {
        console.log(e)
        return e
      })
  },
  updatePaymentNumber (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    let { id, ...data } = payload
    return http.patch(buildUrl(`payments/${id}`, null, instanceUrl), data, { headers })
      .then(r => {
        if (!r || (r && r.hasOwnProperty('ok') && !r.ok)) {
          return Promise.reject(`Unable to save export id`)
        }
        return r.body
      })
      .catch(e => {
        console.log(e)
        return e
      })
  }
}

const getters = {
  getTransactions: (state, rootState) => {
    return [
      ...rootState.getJes,
      ...rootState.getAps,
      ...rootState.getPos,
      ...rootState.getPcs,
      ...rootState.getChecks
    ]
  },
  getTransactionsLoadingStatus: (state) => state.receivingTransactions,
  getPostableTransactions: (state) => {
    return state.transactions
  },
  getTransactionById: (state) => (transactionId) => {
    return this.getTransactions.find(v => v.ledger === transactionId)
  },
  getTransactionTypeByLedgerId: (state, rootState) => (ledgerId) => {
    const aps = rootState.getAps.map(v => ({ id: v.id, ledgerId: v.ledger, type: 'Accounts Payable' }))
    const jes = rootState.getJes.map(v => ({ id: v.id, ledgerId: v.ledger, type: 'Journal Entry' }))
    const pos = rootState.getPos.map(v => ({ id: v.id, ledgerId: v.ledger, type: 'Purchase Order' }))
    const ars = rootState.getArs.map(v => ({ id: v.id, ledgerId: v.ledger, type: 'Accounts Receivable' }))
    const checks = rootState.getChecks.map(v => ({ id: v.id, ledgerId: v.ledger, type: 'Check' }))
    if (aps || jes || pos || checks || ars) {  // TODO: jes, aps, and pos are all empty arrays when entering `/ledger`
      const transaction = [...aps, ...jes, ...pos, ...checks, ...ars].find(x => x.ledgerId === ledgerId)
      if (transaction) return transaction.type
    }
    return null
  },
  getTransactionsToBePosted: (state) => {
    if (state.transactions) return state.transactions
  },
  getReports: (state) => {
    if (state.report) return state.report
  },
  getReportMetadata: (state) => {
    if (state.count) return { count: 1 }
  },
  getReportById: (state) => (id) => {
    return state.report && state.report[id] ? state.report[id] : {}
  },
  getReportFetchStatus: (state) => {
    return state.receivingReport
  },
  getReportError: (state) => {
    return state.receiveReportFailure
  },
  getReportViewStatus: (state) => {
    return state.reportViewStatus
  },
  getReportRenderStatus: (state) => {
    return state.renderingReport
  },
  getTransactionsShown: (state) => {
    return state.transactionsShown
  },
  getCurrentReportId: (state) => {
    return state.currentReportId
  },
  getSelectedTransactions: (state) => {
    return state.transactionSelection
  },
  getFilteredSelectedTransactions: (state) => {
    return state.filteredTransactionSelection
  },
  getCachedParamsById: (state) => (id) => {
    return state.cachedParams[id] || {}
  },
  getPostBtnVisibleStatus: (state) => {
    return state.postBtnVisible
  }
}

const mutations = {
  [RECEIVE_TRANSACTIONS] (state, payload) {
    const { data } = payload
    for (let p in data) {
      const i = state.transactions.findIndex(v => v.ledger === data[p].ledger)
      if (i > -1) {
        state.transactions[i] = data[p]
      } else {
        state.transactions.push(data[p])
      }
    }
    state.transactions = state.transactions
      .sort((a, b) => {   // sort transactions by descending ledger id
        if (a.ledger && b.ledger) return b.ledger - a.ledger
      })
      .map((v, i) => {    // and set page number
        v['page'] = Math.ceil((i + 1) / 20)
        return v
      })
  },
  [RECEIVING_TRANSACTIONS] (state, payload) {
    state.receivingTransactions = payload
  },
  [RECEIVE_REPORT] (state, payload) {
    if (!payload) return state.report
    const { type, reportId, data, transactions, originalParams } = payload
    let tmpReports = Object.assign({}, state.report)
    const reportPdfBlob = b64toBlob(data, 'application/pdf')
    const pdfUrl = URL.createObjectURL(reportPdfBlob)
    if (Object.keys(tmpReports).length > 3) {   // Cache last 4 reports
      let ret = []
      for (let i in tmpReports) {
        ret.push({ t: tmpReports[i].timestamp, id: i })
      }
      const oldest = ret.sort((a, b) => a.t > b.t)[0]
      delete tmpReports[oldest.id]
    }
    tmpReports[reportId] = {
      type,
      data,
      transactions,
      originalParams,
      pdfUrl,
      timestamp: Math.round((new Date()).getTime() / 1000)
    }
    state.report = Object.assign({}, tmpReports)
  },
  [RECEIVING_REPORT] (state, payload) {
    state.receivingReport = payload
  },
  [RECEIVING_REPORTS] (state, payload) {
    state.receivingReports = payload
  },
  [RECEIVE_REPORT_FAILURE] (state, payload) {
    state.receiveReportFailure = payload
  },
  [RECEIVE_REPORTS_FAILURE] (state, payload) {
    state.receiveReportsFailure = payload
  },
  [RECEIVE_REPORT_METADATA] (state, payload) {
    state.reportsMetadata = payload
  },
  [SET_REPORT_VIEW_STATUS] (state, payload) {
    const { reportType, id, isViewed } = payload
    let tmp = Object.assign({}, state.reportViewStatus)
    if (id) tmp[id] = { reportType, isViewed }
    state.reportViewStatus = Object.assign({}, tmp)
  },
  [CACHING_PARAMS] (state, payload) {
    state.cachingParams = payload
  },
  [CACHE_PARAM_FAILURE] (state, payload) {
    state.cacheParamFailure = payload
  },
  [RECEIVE_CACHED_PARAMS] (state, payload) {
    if (!payload || !Object.keys(payload).length) return state.cachedParams
    if (state.cachedParams && Object.keys(state.cachedParams).length) {
      state.cachedParams = Object.assign({}, state.cachedParams, payload)                       // https://vuejs.org/v2/guide/reactivity.html#Change-Detection-Caveats
    } else {
      state.cachedParams = payload
    }
  },
  [RECEIVING_CACHED_PARAMS] (state, payload) {
    state.receivingCachedParams = payload
  },
  [RECEIVE_CACHED_PARAMS_FAILURE] (state, payload) {
    state.receiveCachedParamsFailure = payload
  },
  [RENDERING_REPORT] (state, payload) {
    state.renderingReport = payload
  },
  [UPDATE_TRANSACTION_SELECTION] (state, payload) {
    state.transactionSelection = payload
  },
  [UPDATE_FILTERED_TRANSACTION_SELECTION] (state, payload) {
    state.filteredTransactionSelection = payload
  },
  [UPDATE_TRANSACTIONS_SHOWN] (state, payload) {
    state.transactionsShown = payload
  },
  [UPDATE_CURRENT_REPORT_ID] (state, payload) {
    state.currentReportId = payload
  },
  [CLEAR_TRANSACTIONS] (state) {
    state.transactions = []
  },
  [RESET_REPORT_STATE] (state) {
    state.report = Object.assign({})
    state.reportViewStatus = Object.assign({})
    state.renderingReport = false
    state.receivingReports = false
    state.currentReportId = null
    state.cachedParams = Object.assign({})
    state.cachingParams = Object.assign({}, { status: false, type: '', reportId: '' })
    state.receiveCachedParamsFailure = false
    state.receiveReportFailure = null
    state.receiveReportsFailure = null
    state.postBtnVisible = true
  },
  [SET_POST_BTN_VISIBILITY] (state, payload) {
    state.postBtnVisible = payload
  },
  [CLEAR_STATE] (state) {
    state.data = Object.assign({})
    state.report = Object.assign({})
    state.receivingReport = null
    state.receivingReports = false
    state.renderingReport = false
    state.receiveReportFailure = null
    state.receiveReportsFailure = null
    state.reportViewStatus = Object.assign({})
    state.cachedParams = Object.assign({})
    state.cachingParams = { status: false, type: '', reportId: '' }
    state.cacheParamFailure = false
    state.receivingCachedParams = false
    state.receiveCachedParamsFailure = false
    state.triggerReportAction = false
    state.transactions = []
    state.transactionSelection = []
    state.filteredTransactionSelection = []
    state.transactionsShown = []
    state.currentReportId = null
    state.postBtnVisible = true
    state.reportPdfUrl = null
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
